import { create } from 'zustand';
import { announcementService } from '../services';
import type { AnnouncementDto, AnnouncementListItemDto, CreateAnnouncementDto, UpdateAnnouncementDto } from '../types';
import { getApiErrorMessage } from '@/lib/getApiErrorMessage';

interface AnnouncementState {
  // Active list (visible to every user)
  activeAnnouncements: AnnouncementDto[];
  isLoadingActive: boolean;
  activeError: string | null;

  // Admin list (all statuses; lightweight projection)
  allAnnouncements: AnnouncementListItemDto[];
  isLoadingAll: boolean;
  allError: string | null;

  // Mutation state
  isSubmitting: boolean;
  submitError: string | null;

  // Actions
  fetchActive: () => Promise<void>;
  fetchAll: () => Promise<void>;
  create: (payload: CreateAnnouncementDto) => Promise<boolean>;
  update: (id: number, payload: UpdateAnnouncementDto) => Promise<boolean>;
  remove: (id: number) => Promise<boolean>;
  clearError: () => void;
}

export const useAnnouncementStore = create<AnnouncementState>()((set, get) => ({
  activeAnnouncements: [],
  isLoadingActive: false,
  activeError: null,

  allAnnouncements: [],
  isLoadingAll: false,
  allError: null,

  isSubmitting: false,
  submitError: null,

  fetchActive: async () => {
    set({ isLoadingActive: true, activeError: null });
    try {
      const items = await announcementService.getActive();
      set({ activeAnnouncements: items, isLoadingActive: false });
    } catch (error) {
      set({
        activeError: getApiErrorMessage(error, 'Failed to fetch active announcements'),
        isLoadingActive: false,
      });
    }
  },

  fetchAll: async () => {
    set({ isLoadingAll: true, allError: null });
    try {
      const items = await announcementService.getAll();
      set({ allAnnouncements: items, isLoadingAll: false });
    } catch (error) {
      set({
        allError: getApiErrorMessage(error, 'Failed to fetch announcements'),
        isLoadingAll: false,
      });
    }
  },

  create: async (payload) => {
    set({ isSubmitting: true, submitError: null });
    try {
      await announcementService.create(payload);
      set({ isSubmitting: false });
      // Refresh both lists so the badge and admin table stay accurate
      await Promise.all([get().fetchAll(), get().fetchActive()]);
      return true;
    } catch (error) {
      set({
        submitError: getApiErrorMessage(error, 'Failed to create announcement'),
        isSubmitting: false,
      });
      return false;
    }
  },

  update: async (id, payload) => {
    set({ isSubmitting: true, submitError: null });
    try {
      await announcementService.update(id, payload);
      set({ isSubmitting: false });
      await Promise.all([get().fetchAll(), get().fetchActive()]);
      return true;
    } catch (error) {
      set({
        submitError: getApiErrorMessage(error, 'Failed to update announcement'),
        isSubmitting: false,
      });
      return false;
    }
  },

  remove: async (id) => {
    set({ isSubmitting: true, submitError: null });
    try {
      await announcementService.delete(id);
      set({ isSubmitting: false });
      await Promise.all([get().fetchAll(), get().fetchActive()]);
      return true;
    } catch (error) {
      set({
        submitError: getApiErrorMessage(error, 'Failed to delete announcement'),
        isSubmitting: false,
      });
      return false;
    }
  },

  clearError: () => set({ activeError: null, allError: null, submitError: null }),
}));

// Critical-priority announcements are derived inside components via useMemo on the
// `activeAnnouncements` array. We deliberately do NOT expose a derived selector here:
// `.filter()` would return a new array reference each render, which Zustand v5's
// useSyncExternalStore semantics treat as a state change and would trigger an infinite
// re-render loop ("getSnapshot should be cached" warning).
